import { useCallback, useContext, useEffect, useState } from 'react';
import betslip from '@gin-betting/betslip';
import core, { GQL, View } from '@gin-betting/core';
import { first, floor, includes, toNumber } from 'lodash';
import { createSelector } from 'reselect';

import { EventType } from 'app-constants';
import { useEventHandler, useMemoSelector, usePollingMinStake } from 'hooks';
import { NOT_ENOUGH_BALANCE_REASON_LIST } from '../../constants';
import { useChangeStake, usePlaceBet } from '../../hooks';
import { RestrictionsContext } from '../../restrictions';

const {
    entities: {
        selectors: { selectBets },
    },
} = core;

const {
    selectors: { selectBetslipOddsByChosenBetslipBet },
} = betslip;

interface Output {
    handleCancel: VoidFn;
    isModalOpen: boolean;
    handleRefillBalance: VoidFn;
    missingBalance: number;
    placeBet: VoidFn;
    actualBalance: number;
    isStakeBtn: boolean;
}

interface Input {
    betslipBetId: string;
}

const useRefillBalanceModal = ({ betslipBetId }: Input): Output => {
    const [isModalOpen, seIsModalOpen] = useState<boolean>(false);
    const { state: restrictionsState } = useContext(RestrictionsContext);

    const { minStake } = usePollingMinStake();

    const betslipBet = useMemoSelector(makeSelectBetslipBet(), {
        betslipBetId,
        restrictionsState,
    });

    const stake = toNumber(betslipBet?.stake);

    const actualBalance = getBalance(betslipBet);

    const isStakeBtn = actualBalance >= minStake;

    const changeStakeHandler = useChangeStake({ betslipBetId });

    const { placeBetHandler } = usePlaceBet({
        betslipBetId,
        totalStake: stake,
    });

    const { dispatchBettingEvent, isEventAvailable } = useEventHandler({
        eventType: EventType.OpenDeposit,
    });

    const missingBalance = floor(stake - actualBalance, 2);

    const placeBet = useCallback(() => {
        changeStakeHandler(`${actualBalance}`);

        setTimeout(() => {
            placeBetHandler();
        }, 350);
    }, [actualBalance, changeStakeHandler, placeBetHandler]);

    const handleRefillBalance = useCallback(() => {
        if (!isEventAvailable) return;

        dispatchBettingEvent({ deposit: `${missingBalance}` });
    }, [dispatchBettingEvent, isEventAvailable, missingBalance]);

    const handleCancel = () => seIsModalOpen(false);

    useEffect(() => {
        seIsModalOpen(!!betslipBet);
    }, [betslipBet]);

    return {
        handleCancel,
        isModalOpen,
        handleRefillBalance,
        missingBalance,
        placeBet,
        actualBalance,
        isStakeBtn,
    };
};

const makeSelectBetslipBet = () => {
    return createSelector(
        selectBetslipOddsByChosenBetslipBet,
        selectBets,
        (betslipOdds, bets) => {
            return betslipOdds
                .map(({ betId }) => {
                    if (!betId) return;

                    const bet = bets.get(betId);

                    if (!bet) return;

                    return bet;
                })

                .find(bet =>
                    includes(NOT_ENOUGH_BALANCE_REASON_LIST, bet?.declineReason)
                );
        }
    );
};

function getBalance(bet: View.Bet | undefined): number {
    if (!bet) return 0;

    const declineContextItem = first(bet.declineContext) as
        | GQL.BetDeclineContextRestrictions
        | undefined;

    if (!declineContextItem) return 0;

    const res = declineContextItem.restrictions.find(
        r => r.type === GQL.RestrictionType.not_enough_balance
    ) as GQL.NotEnoughBalanceRestriction | undefined;

    if (!res) return 0;

    return floor(+res.actualBalance, 2);
}

export default useRefillBalanceModal;
